import { LayoutColumn } from "@/lib/utils/lomdaV3/types";

export const MIN_COLUMN_WIDTH = 10;

const toShares = (values: number[]): number[] => {
    const total = values.reduce((sum, v) => sum + v, 0);
    return total > 0
        ? values.map((v) => (v * 100) / total)
        : values.map(() => 100 / values.length);
};

export const columnShares = (columns: LayoutColumn[]): number[] =>
    toShares(columns.map((c) => c.width ?? 100 / columns.length));

export const wholePercents = (values: number[]): number[] => {
    const shares = toShares(values);
    const whole = shares.map(Math.floor);
    let left = 100 - whole.reduce((sum, v) => sum + v, 0);
    const byRemainder = shares
        .map((_, i) => i)
        .sort((a, b) => shares[b] - whole[b] - (shares[a] - whole[a]) || a - b);
    for (const i of byRemainder) {
        if (left <= 0) break;
        whole[i] += 1;
        left -= 1;
    }
    return whole;
};

/** A column narrower than the minimum (saved before it existed) keeps its width. */
export const columnMin = (width: number) => Math.min(MIN_COLUMN_WIDTH, width);

const neighbourOf = (index: number, count: number) =>
    index < count - 1 ? index + 1 : index - 1;

export const columnWidthRange = (widths: number[], index: number) => {
    const j = neighbourOf(index, widths.length);
    return {
        min: columnMin(widths[index]),
        max: widths[index] + widths[j] - columnMin(widths[j]),
    };
};

export const withColumnWidth = (
    columns: LayoutColumn[],
    index: number,
    value: number
): LayoutColumn[] | null => {
    const widths = wholePercents(columnShares(columns));
    const { min, max } = columnWidthRange(widths, index);
    const v = Math.min(max, Math.max(min, Math.round(value)));
    if (v === widths[index]) return null;
    widths[neighbourOf(index, widths.length)] -= v - widths[index];
    widths[index] = v;
    return withWidths(columns, widths);
};

export const withWidths = (
    columns: LayoutColumn[],
    widths: number[]
): LayoutColumn[] | null =>
    columns.every((c, i) => c.width === widths[i])
        ? null
        : columns.map((c, i) => ({ ...c, width: widths[i] }));
